import {
  Checkbox,
  Divider,
  LoadingSpinner,
  Text,
  TextButton,
} from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
// eslint-disable-next-line no-restricted-imports -- TextField is needed for multiline input
import { DialogContent, FormControlLabel, Stack, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import { type Facility } from "../../data/schemas";

const SHIFT_TYPE_OPTIONS = [
  { value: "day", label: "Day Shift" },
  { value: "evening", label: "Evening Shift" },
  { value: "night", label: "Night Shift" },
  { value: "weekend", label: "Weekend Shift" },
] as const;

const shiftPreferencesSchema = z.object({
  preferredShiftTypes: z.array(z.string()).min(1, "Please select at least one shift type"),
  additionalNotes: z.string().optional(),
});

export type ShiftPreferencesFormValues = z.infer<typeof shiftPreferencesSchema>;

interface ShiftPreferencesSheetProps {
  facility: Facility;
  isSubmitting: boolean;
  onSubmit: (data: ShiftPreferencesFormValues) => void;
  onBack: () => void;
}

export function ShiftPreferencesSheet({
  facility,
  isSubmitting,
  onSubmit,
  onBack,
}: ShiftPreferencesSheetProps) {
  const methods = useForm<ShiftPreferencesFormValues>({
    resolver: zodResolver(shiftPreferencesSchema),
    defaultValues: {
      preferredShiftTypes: [],
      additionalNotes: "",
    },
  });

  const { handleSubmit, control } = methods;

  return (
    <Stack>
      <DialogContent>
        <Stack spacing={4}>
          <Stack spacing={2} alignItems="center">
            <Text semibold variant="h3" sx={{ textAlign: "center" }}>
              What shifts interest you?
            </Text>
            <Text
              variant="body1"
              color={(theme) => theme.palette.text.secondary}
              sx={{ textAlign: "center" }}
            >
              Select the shift types you&apos;d be interested in working at {facility.name}.
            </Text>
          </Stack>

          <Stack spacing={4}>
            <Text semibold variant="body1">
              Preferred Shift Types *
            </Text>
            <Controller
              name="preferredShiftTypes"
              control={control}
              render={({ field, fieldState }) => (
                <Stack spacing={3}>
                  {SHIFT_TYPE_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      label={
                        <Text variant="body1" color="text.primary" ml={4}>
                          {option.label}
                        </Text>
                      }
                      control={
                        <Checkbox
                          checked={field.value.includes(option.value)}
                          onChange={() => {
                            const isCurrentlyChecked = field.value.includes(option.value);
                            const newValue = isCurrentlyChecked
                              ? field.value.filter((v) => v !== option.value)
                              : [...field.value, option.value];
                            field.onChange(newValue);
                          }}
                        />
                      }
                    />
                  ))}
                  {fieldState.error && (
                    <Text variant="body2" color="error">
                      {fieldState.error.message}
                    </Text>
                  )}
                </Stack>
              )}
            />
          </Stack>

          <Stack spacing={4}>
            <Divider />
            <Stack spacing={3}>
              <Text semibold variant="body2">
                Additional Notes (Optional)
              </Text>
              <Controller
                name="additionalNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    fullWidth
                    rows={4}
                    variant="outlined"
                    placeholder="Any other information you'd like to share..."
                  />
                )}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={isSubmitting}
          onClick={handleSubmit((data: ShiftPreferencesFormValues) => {
            onSubmit(data);
          })}
        >
          {isSubmitting ? <LoadingSpinner size="small" /> : "Submit Interest"}
        </Button>
        <TextButton size="large" LinkComponent="button" disabled={isSubmitting} onClick={onBack}>
          Back
        </TextButton>
      </DialogFooter>
    </Stack>
  );
}
